import { useCallback, useEffect, useRef, useState } from 'react';

type FilePreviewState = {
    open: boolean;
    url: string | null;
    contentType: string | null;
    generating: boolean;
};

const initialState: FilePreviewState = {
    open: false,
    url: null,
    contentType: null,
    generating: false,
};

const useFilePreview = () => {
    const [previewState, setPreviewState] = useState<FilePreviewState>(initialState);
    const previewUrlRef = useRef<string | null>(null);

    const openPreviewWithBlob = useCallback((blob: Blob) => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
        }
        const url = URL.createObjectURL(blob);
        previewUrlRef.current = url;
        setPreviewState({ open: true, url, contentType: blob.type || null, generating: false });
    }, []);

    const openPreviewWithUrl = useCallback((url: string, contentType: string | null = null) => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
        setPreviewState({ open: true, url, contentType, generating: false });
    }, []);

    const closePreview = useCallback(() => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
        setPreviewState(initialState);
    }, []);

    const setGenerating = useCallback((generating: boolean) => {
        setPreviewState((s) => ({ ...s, generating }));
    }, []);

    useEffect(() => {
        return () => {
            if (previewUrlRef.current) {
                URL.revokeObjectURL(previewUrlRef.current);
            }
        };
    }, []);

    return { previewState, openPreviewWithBlob, openPreviewWithUrl, closePreview, setGenerating };
};

export default useFilePreview;
